import {useRef} from 'react';

import {FILE_UPLOAD_ACCEPTED_FORMATS} from './file-upload.constants';
import {useFileDragDrop} from '../../hooks/useFileDragDrop';
import {cn} from '../../lib/utils';
import {Icon} from '../Icon/Icon';
import {Typography} from '../Typography/Typography';

export interface FileUploadZoneProps {
  onFilesSelect: (files: File[]) => void;
  acceptedFormats?: string;
  clickLabel?: string;
  dragLabel?: string;
  maxSizeLabel?: string;
  multiple?: boolean;
  disabled?: boolean;
  /** Shows the error visual treatment — a red border — e.g. after a rejected file. */
  hasError?: boolean;
  /** Called when zone is clicked while disabled (e.g. max files reached). Overrides the default no-op. */
  onDisabledClick?: () => void;
}

export function FileUploadZone({
  onFilesSelect,
  acceptedFormats = FILE_UPLOAD_ACCEPTED_FORMATS,
  clickLabel = 'Click to upload',
  dragLabel = 'or drag and drop',
  maxSizeLabel = 'PDF, PNG, JPG, WEBP',
  multiple = false,
  disabled = false,
  hasError = false,
  onDisabledClick
}: FileUploadZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const {isDragActive, dragProps} = useFileDragDrop({disabled, multiple, onFilesSelect});

  const handleClick = () => {
    if (disabled) {
      onDisabledClick?.();
      return;
    }
    inputRef.current?.click();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length > 0) {
      onFilesSelect(files);
      e.target.value = '';
    }
  };

  return (
    <div
      data-slot="file-upload-zone"
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      onClick={handleClick}
      onKeyDown={e => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        if (e.key === ' ') e.preventDefault();
        handleClick();
      }}
      {...dragProps}
      className={cn(
        'relative flex w-full cursor-pointer flex-col items-center gap-3 rounded-xl border bg-background-surface px-6 py-4 transition-[border-color,background-color] duration-500 ease-in-out',
        hasError
          ? 'border-border-error hover:border-border-error'
          : isDragActive
            ? 'border-brand-primary bg-background-secondary'
            : 'border-border-subtle hover:border-brand-primary hover:bg-background-secondary',
        disabled &&
          'cursor-not-allowed opacity-80 hover:border-border-subtle hover:bg-background-surface'
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept={acceptedFormats}
        multiple={multiple}
        className="sr-only"
        onChange={handleChange}
        disabled={disabled}
        tabIndex={-1}
      />

      {/* Featured icon */}
      <div className="relative size-10 overflow-clip rounded-md border border-border bg-background-surface shadow-xs-skeumorphic">
        <Icon
          name="upload"
          className="absolute left-1/2 top-1/2 size-5 -translate-x-1/2 -translate-y-1/2 text-fg-secondary"
        />
      </div>

      {/* Labels */}
      <div className="flex w-full flex-col items-center gap-1">
        <div className="flex items-center gap-1">
          <span className="text-sm font-semibold text-brand-accent">{clickLabel}</span>
          <span className="text-sm text-fg-tertiary">{dragLabel}</span>
        </div>
        <Typography size="xs" color="tertiary" className="text-center whitespace-normal">
          {maxSizeLabel}
        </Typography>
      </div>
    </div>
  );
}
